import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Calculator, ArrowRight, Loader2, Zap, Info, Sparkles, Share2, Copy, FileDown, X, History, GitCompare, Landmark } from "lucide-react";
import { FinanciamientoModal } from "@/components/aliados/FinanciamientoModal";
import { CuotaEstimada } from "@/components/aliados/CuotaEstimada";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { CostBreakdown } from "@/components/CostBreakdown";
import { PlatesComparator } from "@/components/PlatesComparator";
import { toast } from "sonner";
import api, { clp } from "@/lib/api";
import { formatCLP, parseCLP } from "@/lib/clp";
import { getRecent, addRecent, removeRecent } from "@/lib/recentPlates";
import { useAuth } from "@/context/AuthContext";
import { useCountry } from "@/context/CountryContext";
import { SimulatorInternacional } from "@/components/SimulatorInternacional";

const REGIONES = [
  "Arica y Parinacota", "Tarapacá", "Antofagasta", "Atacama", "Coquimbo",
  "Valparaíso", "Metropolitana", "O'Higgins", "Maule", "Ñuble", "Biobío",
  "La Araucanía", "Los Ríos", "Los Lagos", "Aysén", "Magallanes",
];

export const SimulatorCard = () => {
  const { pais } = useCountry();
  if (pais !== "CL") return <SimulatorInternacional />;
  return <SimulatorChile />;
};

const SimulatorChile = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [form, setForm] = useState({
    patente: "", valor: 8500000, avaluo_fiscal: "", region: "Metropolitana",
    requiere_notaria: true, requiere_informe: true, requiere_express: false,
  });
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingAvaluo, setLoadingAvaluo] = useState(false);
  const [avaluoMeta, setAvaluoMeta] = useState(null);
  const [shareData, setShareData] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [recent, setRecent] = useState([]);
  const [compareIds, setCompareIds] = useState([]);  // patentes seleccionadas para comparar (max 2)
  const [showComparator, setShowComparator] = useState(false);
  const [financiador, setFinanciador] = useState(null);
  const [showFin, setShowFin] = useState(false);
  useEffect(() => { api.get("/api/aliados/financiador").then(({ data }) => setFinanciador(data.financiador)).catch(() => {}); }, []);
  const lastAutoPlate = useRef("");  // evita re-disparos para la misma patente

  // sync remoto silencioso para usuarios autenticados
  const syncRemote = (items) => {
    if (!user) return;
    api.post("/api/me/recent-plates", { items }).catch(() => {/* sync silencioso */});
  };

  // carga historial inicial desde localStorage + sync con backend si hay usuario
  useEffect(() => {
    const local = getRecent();
    setRecent(local);
    if (!user) return;
    api.get("/api/me/recent-plates").then(({ data }) => {
      const server = Array.isArray(data?.items) ? data.items : [];
      if (server.length > 0) {
        setRecent(server);
        try { localStorage.setItem("tf_recent_plates", JSON.stringify(server)); } catch {/* ignore */}
      } else if (local.length > 0) {
        api.post("/api/me/recent-plates", { items: local }).catch(() => {});
      }
    }).catch(() => {/* offline o no-auth: usa cache local */});
  }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const autoAvaluo = async (overridePlate, silent = false) => {
    const plate = (overridePlate ?? form.patente ?? "").trim().toUpperCase();
    if (plate.length < 4) {
      if (!silent) toast.error("Ingresa primero una patente válida (ej: ABCD12)");
      return;
    }
    setLoadingAvaluo(true);
    setAvaluoMeta(null);
    try {
      const { data } = await api.post("/api/simulator/avaluo", { patente: plate });
      if (!data.ok) {
        if (!silent) toast.error(data.error || "No pudimos estimar el avalúo");
        return;
      }
      setForm((f) => ({ ...f, avaluo_fiscal: data.avaluo_fiscal_estimado }));
      setAvaluoMeta(data);
      // guarda en historial localStorage + sync backend si auth
      const updated = addRecent({
        patente: plate,
        avaluo: data.avaluo_fiscal_estimado,
        tasacion: data.tasacion_mercado,
        marca: data.vehiculo?.marca || "",
        modelo: data.vehiculo?.modelo || "",
        anio: data.vehiculo?.anio || null,
      });
      setRecent(updated);
      syncRemote(updated);
      if (!silent) toast.success(`Avalúo estimado: $${data.avaluo_fiscal_estimado.toLocaleString("es-CL")}`);
    } catch (err) {
      if (!silent) toast.error("No pudimos consultar el vehículo. Intenta de nuevo en unos segundos.");
    } finally {
      setLoadingAvaluo(false);
    }
  };

  // Auto-disparo: cuando la patente alcanza 6 caracteres válidos, consulta avalúo + tasación
  // tras un debounce de 600ms (evita disparos mientras el usuario sigue tipeando).
  useEffect(() => {
    const plate = (form.patente || "").trim().toUpperCase();
    const valid = /^[A-Z]{2,4}\d{2}$/.test(plate) && plate.length === 6;
    if (!valid || plate === lastAutoPlate.current || loadingAvaluo) return;
    const t = setTimeout(() => {
      lastAutoPlate.current = plate;
      autoAvaluo(plate, true);  // silent: sin toast
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.patente]);

  const loadFromRecent = (item) => {
    lastAutoPlate.current = "";  // permite re-disparar la consulta
    setForm((f) => ({ ...f, patente: item.patente, avaluo_fiscal: item.avaluo || "" }));
    if (item.avaluo) {
      setAvaluoMeta({
        avaluo_fiscal_estimado: item.avaluo,
        tasacion_mercado: item.tasacion || 0,
        tasacion_min: item.tasacion ? Math.round(item.tasacion * 0.9) : 0,
        tasacion_max: item.tasacion ? Math.round(item.tasacion * 1.1) : 0,
        vehiculo: { marca: item.marca, modelo: item.modelo, anio: item.anio },
        ai: true, confianza: "cache",
      });
    }
    toast.success(`Cargado desde historial: ${item.patente}`);
  };

  const removeFromRecent = (e, plate) => {
    e.stopPropagation();
    const next = removeRecent(plate);
    setRecent(next);
    setCompareIds((ids) => ids.filter((p) => p !== plate));
    syncRemote(next);
  };

  const toggleCompare = (e, plate) => {
    e.stopPropagation();
    setCompareIds((ids) => {
      if (ids.includes(plate)) return ids.filter((p) => p !== plate);
      if (ids.length >= 2) {
        toast.info("Máximo 2 vehículos por comparación");
        return ids;
      }
      return [...ids, plate];
    });
  };

  const compareItems = recent.filter((r) => compareIds.includes(r.patente));

  const simular = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/api/simulator/quote", {
        patente: form.patente, valor: Number(form.valor),
        avaluo_fiscal: form.avaluo_fiscal ? Number(form.avaluo_fiscal) : null,
        region: form.region, requiere_notaria: form.requiere_notaria,
        requiere_informe: form.requiere_informe, requiere_express: form.requiere_express,
      });
      setQuote(data);
    } finally {
      setLoading(false);
    }
  };

  const iniciar = () => {
    if (user) navigate("/nueva-transferencia", { state: { prefill: form } });
    else login();
  };

  const compartir = async () => {
    setSharing(true);
    try {
      const { data } = await api.post("/api/cotizacion/share", {
        quote_req: {
          patente: form.patente, valor: Number(form.valor),
          avaluo_fiscal: form.avaluo_fiscal ? Number(form.avaluo_fiscal) : null,
          region: form.region, requiere_notaria: form.requiere_notaria,
          requiere_informe: form.requiere_informe, requiere_express: form.requiere_express,
        },
        vehicle: avaluoMeta?.vehiculo || null,
      });
      if (!data.ok) { toast.error("No pudimos crear el link"); return; }
      const fullUrl = `${window.location.origin}/cotizacion/${data.code}`;
      setShareData({ ...data, full_url: fullUrl });
    } catch {
      toast.error("Error al generar el link de cotización");
    } finally {
      setSharing(false);
    }
  };

  const copyUrl = async () => {
    if (!shareData) return;
    try {
      await navigator.clipboard.writeText(shareData.full_url);
      toast.success("Link copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar; selecciona y copia manualmente");
    }
  };

  return (
    <div className="tf-card p-5 shadow-2xl shadow-[#13315C]/20 text-[#1F2937]" data-testid="simulator-card">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-8 w-8 rounded-md bg-[#13315C] flex items-center justify-center">
          <Calculator size={16} className="text-white" />
        </div>
        <div>
          <p className="tf-label text-[10px]">Simulador</p>
          <h3 className="font-display font-bold text-[#13315C] text-base leading-tight">Calcula tu transferencia</h3>
        </div>
      </div>

      <div className="space-y-2.5">
        <div className="grid grid-cols-2 gap-2.5 items-start">
          <div className="flex flex-col">
            <Label className="text-[11px] font-semibold text-slate-500 flex items-center gap-1 h-4 leading-4">
              <span>Patente</span>
              {loadingAvaluo && <Loader2 size={10} className="animate-spin text-[#F5B301]" data-testid="sim-patente-auto-spinner" />}
              {!loadingAvaluo && !avaluoMeta && (
                <span className="text-[9px] font-bold text-[#F5B301] uppercase tracking-wide">⚡ IA</span>
              )}
            </Label>
            <Input
              data-testid="sim-patente"
              value={form.patente}
              onChange={(e) => set("patente", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
              placeholder="ABCD12" maxLength={6}
              className="mt-1 h-10 uppercase text-sm font-mono-tf tracking-wider"
            />
          </div>
          <div className="flex flex-col">
            <Label className="text-[11px] font-semibold text-slate-500 flex items-center gap-1 h-4 leading-4">
              <span>Valor de venta (CLP)</span>
            </Label>
            <Input
              data-testid="sim-valor"
              type="text"
              inputMode="numeric"
              value={formatCLP(form.valor)}
              onChange={(e) => set("valor", parseCLP(e.target.value))}
              placeholder="8.500.000"
              className="mt-1 h-10 text-sm font-mono-tf"
            />
            {avaluoMeta && avaluoMeta.tasacion_min > 0 && (() => {
              const v = Number(form.valor) || 0;
              const min = avaluoMeta.tasacion_min;
              const max = avaluoMeta.tasacion_max;
              let badge = null;
              if (v > 0) {
                if (v < min) {
                  const pct = Math.round(((min - v) / min) * 100);
                  badge = { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: "🟡", txt: `Bajo el mercado (~${pct}% menos). Podrías subirlo.`, tid: "precio-bajo" };
                } else if (v > max) {
                  const pct = Math.round(((v - max) / max) * 100);
                  badge = { cls: "bg-orange-50 text-orange-700 border-orange-200", icon: "🟠", txt: `Sobre el mercado (~${pct}% más). Considera negociar.`, tid: "precio-alto" };
                } else {
                  badge = { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: "✅", txt: "Precio justo · dentro del rango de mercado.", tid: "precio-justo" };
                }
              }
              return (
                <>
                  <p className="mt-1 text-[10px] text-slate-500 leading-tight" data-testid="sim-rango-mercado">
                    Rango mercado: <span className="font-semibold text-[#13315C]">{clp(min)} – {clp(max)}</span>{" "}
                    <span className="text-[#1F9BD7]">· mediana {clp(avaluoMeta.tasacion_mercado)}</span>
                  </p>
                  {badge && (
                    <div className={`mt-1 rounded-md border px-2 py-1 text-[10px] font-semibold leading-tight ${badge.cls}`} data-testid={`sim-badge-${badge.tid}`}>
                      {badge.icon} {badge.txt}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-[11px] font-semibold text-slate-500 flex items-center gap-1">
              Avalúo fiscal SII (opcional)
              <span title="El 1,5% se cobra sobre este valor, no sobre el precio. Lo encuentras en el permiso de circulación.">
                <Info size={11} className="text-slate-400" />
              </span>
            </Label>
            <button
              type="button"
              onClick={() => { lastAutoPlate.current = ""; autoAvaluo(); }}
              disabled={loadingAvaluo || !form.patente}
              data-testid="sim-avaluo-auto-btn"
              className="text-[11px] font-bold text-[#F5B301] hover:text-[#dca400] disabled:text-slate-300 active:scale-95 transition inline-flex items-center gap-1 px-2 py-1.5 -mr-2 rounded min-h-[32px]"
            >
              {loadingAvaluo ? <Loader2 className="animate-spin" size={12} /> : <Sparkles size={12} />}
              {loadingAvaluo ? "Consultando..." : (avaluoMeta ? "Re-consultar" : "Auto-llenar")}
            </button>
          </div>
          <Input
            data-testid="sim-avaluo"
            type="text"
            inputMode="numeric"
            value={formatCLP(form.avaluo_fiscal)}
            onChange={(e) => { set("avaluo_fiscal", parseCLP(e.target.value)); setAvaluoMeta(null); }}
            placeholder="3.200.000"
            className="mt-0.5 h-9 text-sm font-mono-tf"
          />
          {avaluoMeta && (
            <p className="mt-1 text-[10px] text-slate-500 leading-tight" data-testid="sim-avaluo-meta">
              <Sparkles size={9} className="inline text-[#F5B301]" />{" "}
              <span className="font-semibold text-[#13315C]">
                {avaluoMeta.vehiculo?.marca || ""} {avaluoMeta.vehiculo?.modelo || ""} {avaluoMeta.vehiculo?.anio || ""}
              </span>
              {" — "}{avaluoMeta.ai ? "Estimado IA Claude" : "Estimación determinista"}
            </p>
          )}
        </div>

        {recent.length > 0 && (
          <div data-testid="recent-plates">
            <div className="flex items-center justify-between mb-1.5">
              <span className="flex items-center gap-1.5 text-[10px] font-semibold text-slate-500">
                <History size={10} /> Consultadas recientemente
              </span>
              {compareIds.length === 2 && (
                <button
                  type="button"
                  onClick={() => setShowComparator(true)}
                  data-testid="compare-btn"
                  className="text-[10px] font-bold text-white bg-[#13315C] hover:bg-[#1C4076] rounded px-2 py-0.5 inline-flex items-center gap-1"
                >
                  <GitCompare size={10} /> Comparar 2 vehículos
                </button>
              )}
              {compareIds.length === 1 && (
                <span className="text-[10px] text-slate-400">Marca 1 más para comparar</span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {recent.map((it) => {
                const isSel = compareIds.includes(it.patente);
                return (
                  <button
                    key={it.patente}
                    type="button"
                    onClick={() => loadFromRecent(it)}
                    data-testid={`recent-${it.patente}`}
                    className={`group inline-flex items-center gap-1 rounded-md border px-2 py-1 transition-colors ${
                      isSel
                        ? "bg-[#F5B301]/15 border-[#F5B301] ring-1 ring-[#F5B301]"
                        : "bg-slate-100 hover:bg-[#13315C] hover:text-white border-slate-200"
                    }`}
                    title={`${it.marca || ""} ${it.modelo || ""} ${it.anio || ""} · avalúo $${(it.avaluo || 0).toLocaleString("es-CL")}`}
                  >
                    <span
                      onClick={(e) => toggleCompare(e, it.patente)}
                      className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center cursor-pointer transition-colors ${
                        isSel ? "bg-[#F5B301] border-[#F5B301]" : "border-slate-400 group-hover:border-white"
                      }`}
                      data-testid={`recent-cmp-${it.patente}`}
                      title="Marcar para comparar"
                    >
                      {isSel && <GitCompare size={9} className="text-[#13315C]" strokeWidth={3} />}
                    </span>
                    <span className={`font-mono-tf font-bold text-[10px] tracking-wider ${isSel ? "text-[#13315C]" : "text-[#13315C] group-hover:text-white"}`}>{it.patente}</span>
                    {it.marca && (
                      <span className={`text-[9px] hidden sm:inline ${isSel ? "text-slate-600" : "text-slate-500 group-hover:text-white/70"}`}>{it.marca}</span>
                    )}
                    <span
                      onClick={(e) => removeFromRecent(e, it.patente)}
                      className={`cursor-pointer ml-0.5 hover:!text-red-500 ${isSel ? "text-slate-500" : "text-slate-400 group-hover:text-white/60"}`}
                      data-testid={`recent-remove-${it.patente}`}
                    >
                      <X size={10} />
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div>
          <Label className="text-[11px] font-semibold text-slate-500">Región</Label>
          <select value={form.region} onChange={(e) => set("region", e.target.value)}
            className="mt-0.5 h-11 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            data-testid="sim-region">
            {REGIONES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <label className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-3 min-h-[48px] cursor-pointer active:bg-slate-100 transition-colors">
          <span className="text-xs font-medium text-slate-700">Gestión notarial</span>
          <Switch checked={form.requiere_notaria} onCheckedChange={(v) => set("requiere_notaria", v)} data-testid="sim-notaria" />
        </label>
        <label className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-3 min-h-[48px] cursor-pointer active:bg-slate-100 transition-colors">
          <span className="text-xs font-medium text-slate-700">Informe Transfiérelo 360</span>
          <Switch checked={form.requiere_informe} onCheckedChange={(v) => set("requiere_informe", v)} data-testid="sim-informe" />
        </label>
        <label className={`flex items-center justify-between rounded-md px-3 py-3 min-h-[52px] border transition-colors cursor-pointer ${form.requiere_express ? "bg-[#13315C] border-[#13315C]" : "bg-amber-50 border-amber-200 active:bg-amber-100"}`}>
          <div className="flex items-center gap-2">
            <Zap size={14} className={form.requiere_express ? "text-[#F5B301]" : "text-amber-500"} />
            <div>
              <span className={`text-xs font-semibold ${form.requiere_express ? "text-white" : "text-[#13315C]"}`}>Transferencia Exprés</span>
              <p className={`text-[10px] ${form.requiere_express ? "text-white/70" : "text-amber-600"}`}>Lista en 72 h en vez de +40 días</p>
            </div>
          </div>
          <Switch checked={form.requiere_express} onCheckedChange={(v) => set("requiere_express", v)} data-testid="sim-express" />
        </label>

        <Button onClick={simular} disabled={loading} className="w-full bg-[#13315C] hover:bg-[#1C4076] text-white rounded-md h-11 text-sm font-semibold active:scale-[0.97] transition-transform" data-testid="sim-calcular-btn">
          {loading ? <><Loader2 className="animate-spin mr-1.5" size={16} /> Calculando…</> : "Calcular costo estimado"}
        </Button>
      </div>

      {quote && (
        <div className="mt-4 pt-4 border-t border-slate-200 tf-fade-up">
          <CostBreakdown quote={quote} compact />
          <div className="mt-3 grid grid-cols-[1fr_auto] gap-2">
            <Button onClick={iniciar} className="bg-[#F5B301] hover:bg-[#dca400] text-[#13315C] rounded-md h-10 text-sm font-semibold group" data-testid="sim-iniciar-btn">
              Iniciar transferencia
              <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button onClick={compartir} disabled={sharing} variant="outline"
              className="rounded-md h-10 px-3 text-sm font-semibold border-[#13315C] text-[#13315C] hover:bg-[#13315C] hover:text-white"
              data-testid="sim-share-btn" title="Compartir cotización (URL + PDF firmado)">
              {sharing ? <Loader2 size={15} className="animate-spin" /> : <Share2 size={15} />}
            </Button>
          </div>
          {financiador && (Number(form.valor) > 0
            ? <CuotaEstimada financiador={financiador} valor={Number(form.valor)} patente={form.patente} origen="simulador" compact />
            : (
            <Button onClick={() => setShowFin(true)} variant="outline"
              className="w-full mt-2 rounded-md h-10 text-sm font-semibold border-2 bg-white hover:bg-slate-50"
              style={{ borderColor: financiador.color, color: financiador.color }} data-testid="sim-financiar-btn">
              <Landmark size={15} className="mr-1.5" /> {financiador.texto}
            </Button>
          ))}
        </div>
      )}

      {showFin && financiador && (
        <FinanciamientoModal financiador={financiador} patente={form.patente} valor={Number(form.valor) || null}
          origen="simulador" onClose={() => setShowFin(false)} />
      )}

      {shareData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={() => setShareData(null)} data-testid="share-modal">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-[10px] uppercase tracking-wide text-[#F5B301] font-bold">Cotización compartible</p>
                <h3 className="font-display font-bold text-xl text-[#13315C]">Link generado ✓</h3>
              </div>
              <button onClick={() => setShareData(null)} className="text-slate-400 hover:text-[#13315C]" data-testid="share-close">
                <X size={20} />
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Envía este link al comprador antes de visitar. Verá la cotización exacta firmada por Transfiérelo, válida por 30 días.
            </p>
            <div className="flex items-center gap-2 rounded-md bg-slate-50 border border-slate-200 p-2 mb-3">
              <input
                readOnly
                value={shareData.full_url}
                className="flex-1 bg-transparent text-xs font-mono-tf text-[#13315C] outline-none"
                data-testid="share-url-input"
              />
              <button onClick={copyUrl} className="text-[#13315C] hover:text-[#F5B301]" data-testid="share-copy-btn" title="Copiar URL">
                <Copy size={15} />
              </button>
            </div>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}${shareData.pdf_url}`}
              target="_blank" rel="noreferrer"
              className="w-full flex items-center justify-center gap-2 bg-[#13315C] hover:bg-[#1C4076] text-white rounded-md h-10 text-sm font-semibold"
              data-testid="share-pdf-btn"
            >
              <FileDown size={15} /> Descargar PDF
            </a>
            <p className="text-[10px] text-slate-400 mt-3 text-center">
              Código: <span className="font-mono-tf font-semibold text-[#13315C]">{shareData.code}</span> · expira {new Date(shareData.expira).toLocaleDateString("es-CL")}
            </p>
          </div>
        </div>
      )}
      {showComparator && compareItems.length === 2 && (
        <PlatesComparator
          items={compareItems}
          region={form.region}
          onClose={() => setShowComparator(false)}
        />
      )}
    </div>
  );
};
